import { useEffect, useMemo, useRef, useState } from 'react';
import { Bubble, Prompts, Sender } from '@ant-design/x';
import { Avatar } from 'antd';
import {
    BulbOutlined,
    ExperimentOutlined,
    EyeOutlined,
    RobotOutlined,
    UserOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import {
    addMessage,
    appendToLastAssistant,
    ChatMessage,
    setStreaming,
} from '../../app/chatSlice';
import { buildChatRequest, streamChat } from './chatApi';
import { STARTER_PROMPTS, WELCOME_MESSAGE } from './welcome';
import './Chat.css';

const renderMarkdown = (content: string) => (
    <div className="F-Chat__md">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
);

const ROLE_CONFIG = {
    ai: {
        placement: 'start' as const,
        avatar: <Avatar icon={<RobotOutlined />} style={{ background: '#14d6bc' }} />,
        contentRender: renderMarkdown,
    },
    user: {
        placement: 'end' as const,
        avatar: <Avatar icon={<UserOutlined />} style={{ background: '#4a5160' }} />,
    },
};

const toBubbleRole = (role: 'user' | 'assistant') => (role === 'assistant' ? 'ai' : 'user');

const PROMPT_ICONS = {
    overview: <EyeOutlined style={{ color: '#14d6bc' }} />,
    function: <BulbOutlined style={{ color: '#f5d423' }} />,
    blast: <ExperimentOutlined style={{ color: '#7aa2ff' }} />,
};

const makeId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const formatTime = (ts: number) => {
    const d = new Date(ts);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const Chat = () => {
    const dispatch = useDispatch();
    const messages = useSelector((s: RootState) => s.chat.messages);
    const isStreaming = useSelector((s: RootState) => s.chat.isStreaming);
    const requestId = useSelector((s: RootState) => s.chat.requestId);
    const gffUrl = useSelector((s: RootState) => s.chat.gffUrl);
    const selectedFeature = useSelector((s: RootState) => s.chat.selectedFeature);
    const isSelectedPanelOpen = useSelector((s: RootState) => s.chat.isSelectedPanelOpen);
    const visibleRegion = useSelector((s: RootState) => s.chat.visibleRegion);
    const [draft, setDraft] = useState('');
    const abortRef = useRef<AbortController | null>(null);

    // Cancel any in-flight stream when the component unmounts (e.g. user hits "New job").
    useEffect(() => () => abortRef.current?.abort(), []);

    const canSend = !!requestId && !!gffUrl && !isStreaming;

    const handleSubmit = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || !canSend || !requestId || !gffUrl) return;

        const userMessage: ChatMessage = {
            id: makeId(),
            role: 'user',
            content: trimmed,
            timestamp: Date.now(),
        };
        const assistantPlaceholder: ChatMessage = {
            id: makeId(),
            role: 'assistant',
            content: '',
            timestamp: Date.now() + 1,
        };
        dispatch(addMessage(userMessage));
        dispatch(addMessage(assistantPlaceholder));
        dispatch(setStreaming(true));
        setDraft('');

        const controller = new AbortController();
        abortRef.current = controller;

        const body = buildChatRequest(
            requestId,
            gffUrl,
            trimmed,
            visibleRegion,
            selectedFeature,
            isSelectedPanelOpen
        );

        streamChat(
            body,
            (event) => {
                if (event.type === 'chunk') {
                    dispatch(appendToLastAssistant(event.content));
                } else if (event.type === 'error') {
                    const note = `\n\n_Error: ${event.detail}_`;
                    dispatch(appendToLastAssistant(note));
                }
            },
            controller.signal
        ).finally(() => {
            dispatch(setStreaming(false));
            if (abortRef.current === controller) abortRef.current = null;
        });
    };

    const bubbleItems = useMemo(
        () => [
            {
                key: 'welcome',
                role: 'ai',
                content: WELCOME_MESSAGE,
                footer: <span className="F-Chat__time">{formatTime(Date.now())}</span>,
            },
            ...messages.map((m) => ({
                key: m.id,
                role: toBubbleRole(m.role),
                content: m.content,
                footer: <span className="F-Chat__time">{formatTime(m.timestamp)}</span>,
                loading: m.role === 'assistant' && m.content === '' && isStreaming,
            })),
        ],
        [messages, isStreaming]
    );

    return (
        <div className="F-Chat">
            <div className="F-Chat__messages">
                <Bubble.List autoScroll role={ROLE_CONFIG} items={bubbleItems} />
            </div>

            {messages.length === 0 && (
                <Prompts
                    className="F-Chat__prompts"
                    title="Try asking"
                    vertical
                    items={STARTER_PROMPTS.map((p) => ({
                        key: p.key,
                        icon: PROMPT_ICONS[p.iconName],
                        label: p.text,
                    }))}
                    onItemClick={({ data }) => handleSubmit(String(data.label ?? ''))}
                />
            )}

            <Sender
                className="F-Chat__sender"
                value={draft}
                onChange={setDraft}
                onSubmit={handleSubmit}
                loading={isStreaming}
                submitType="enter"
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder={
                    canSend || isStreaming
                        ? 'Ask anything about the annotation results...'
                        : 'Loading annotation data...'
                }
            />
        </div>
    );
};
